import type { RouterOutputs } from "@repo/api";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import PriceDialog from "~/components/price/price-dialog";
import VatRateDialog from "~/components/vat-rate/vat-rate-dialog";
import { GeneralContext } from "~/hooks/use-general";
import { api } from "~/lib/api";
import { useLoadingStore } from "~/stores/use-loading-store";
import { UnsavedChangesProvider } from "./unsaved-changes-provider";

export type VatRate = RouterOutputs["vatRate"]["getAll"][number];
export type TurnoverGroup = RouterOutputs["turnoverGroup"]["getAll"][number];

export type Price = RouterOutputs["price"]["getAll"][number];
interface GeneralProviderProps {
  children: ReactNode;
}

export function GeneralProvider({ children }: GeneralProviderProps) {
  const { data: turnoverGroups } = api.turnoverGroup.getAll.useQuery(
    undefined,
    {
      initialData: [],
    },
  );

  const { data: vatRates, isFetching: isVatRatesLoading } =
    api.vatRate.getAll.useQuery(undefined, {
      initialData: [],
    });

  const { data: prices, isFetching: isPricesLoading } =
    api.price.getAll.useQuery(undefined, {
      initialData: [],
    });

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  useEffect(() => {
    if (isVatRatesLoading || isPricesLoading) {
      setLoadingStage("general");
    } else {
      setLoadingStage(undefined);
    }
  }, [isPricesLoading, isVatRatesLoading, setLoadingStage]);

  const context = {
    vatRates,
    prices,
    turnoverGroups,
  };

  return (
    <GeneralContext.Provider value={context}>
      {children}
      <UnsavedChangesProvider>
        <VatRateDialog />
      </UnsavedChangesProvider>
      <UnsavedChangesProvider>
        <PriceDialog />
      </UnsavedChangesProvider>
    </GeneralContext.Provider>
  );
}
